/**
 * Social Share Component
 *
 * Manages the share button popover panel.
 * Renders a trigger button that opens a popover listing configured
 * social network share links and a copy-link action.
 *
 * Usage in HTML:
 *   <div data-x-data="socialShare"> ... </div>
 *
 * Exposed state:
 *   - open (boolean): Whether the popover is currently visible
 *   - copyLabel (string): Dynamic label for the copy-link button
 *
 * Exposed methods:
 *   - show(): Opens the popover and traps focus
 *   - hide(): Closes the popover and restores focus
 *   - toggle(): Toggles the popover open/closed
 *   - copyLink(url): Copies url to clipboard and shows feedback
 */
export default function socialShare() {
    return {
        open: false,
        copyLabel: 'Copy Link',
        _previousFocus: null,
        _copyTimeout: null,

        init() {
            // Close on Escape key
            this._onKeydown = (e) => {
                if (e.key === 'Escape' && this.open) this.hide();
            };
            document.addEventListener('keydown', this._onKeydown);
        },

        destroy() {
            document.removeEventListener('keydown', this._onKeydown);
            if (this._copyTimeout) clearTimeout(this._copyTimeout);
        },

        show() {
            this._previousFocus = document.activeElement;
            this.open = true;

            this.$nextTick(() => {
                const closeBtn = this.$el.querySelector('.social-share__close');
                if (closeBtn) closeBtn.focus();
            });
        },

        hide() {
            this.open = false;
            this.$nextTick(() => {
                if (this._previousFocus) this._previousFocus.focus();
            });
        },

        toggle() {
            this.open ? this.hide() : this.show();
        },

        async copyLink(url) {
            if (!url) return;

            try {
                await navigator.clipboard.writeText(url);
                this.copyLabel = 'Copied!';
            } catch {
                // Fallback for older browsers
                const ta = document.createElement('textarea');
                ta.value = url;
                ta.style.position = 'fixed';
                ta.style.opacity = '0';
                document.body.appendChild(ta);
                ta.focus();
                ta.select();
                try {
                    document.execCommand('copy');
                    this.copyLabel = 'Copied!';
                } catch {
                    this.copyLabel = 'Copy failed';
                }
                document.body.removeChild(ta);
            }

            if (this._copyTimeout) clearTimeout(this._copyTimeout);
            this._copyTimeout = setTimeout(() => {
                this.copyLabel = 'Copy Link';
            }, 2500);
        }
    };
}
